import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';

const services = [
  'WiFi site survey and analysis',
  'Signal strength optimization',
  'Channel interference reduction',
  'Access point placement',
  'Mesh network setup',
  'Bandwidth optimization',
  'Network congestion management',
  'Coverage dead zone elimination',
  'Performance monitoring setup',
  'Network capacity planning'
];

const benefits = [
  {
    icon: WifiIcon,
    title: 'Expert Analysis',
    description: 'Professional WiFi coverage assessment'
  },
  {
    icon: SignalCellularAltIcon,
    title: 'Maximum Coverage',
    description: 'Eliminate dead zones and weak spots'
  },
  {
    icon: SpeedIcon,
    title: 'Peak Performance',
    description: 'Optimal speed for all devices'
  },
  {
    icon: SettingsInputAntennaIcon,
    title: 'Smart Setup',
    description: 'Strategic access point placement'
  }
];

const supportedEnvironments = [
  'Home WiFi',
  'Office Networks',
  'Retail Spaces',
  'Warehouses',
  'Educational Facilities',
  'Healthcare Facilities',
  'Outdoor Areas',
  'Multi-floor Buildings',
  'Conference Centers',
  'Public Venues'
];

function WifiOptimization() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            WiFi Optimization Services
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Professional wireless network optimization for maximum coverage and performance
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #0891b2 30%, #22d3ee 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #22d3ee 30%, #0891b2 90%)',
              }
            }}
          >
            Schedule WiFi Analysis
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2, color: 'info.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our WiFi Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Environments Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Environments We Optimize
          </Typography>
          <Grid container spacing={2}>
            {supportedEnvironments.map((env, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography variant="h6">
                    {env}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default WifiOptimization;
